import React, { useEffect, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import AddressDisplay from '../addressdisplay/AddressDisplay';
import { FaSquareFull } from "react-icons/fa";
import './HolderData.css';
import { MdOutlineInfo } from "react-icons/md";

const Info = MdOutlineInfo as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Square = FaSquareFull as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface HolderProps {
  ca: string;
  chain: string;
}

interface HolderDataResponse {
  totalHolders?: number;
  holdersByAcquisition?: {
    swap?: number;
    transfer?: number;
    airdrop?: number;
  };
  holderDistribution?: {
    crab?: number;
    crabs?: number;
    dolphins?: number;
    fish?: number;
    octopus?: number;
    sharks?: number;
    shrimps?: number;
    whales?: number;
  };
  holderChange?: {
    [timeframe: string]: {
      change: number;
      changePercent: number;
    };
  };
  holderSupply?: {
    top10?: { supply?: number; supplyPercent?: number };
    top25?: { supply?: number; supplyPercent?: number };
    top50?: { supply?: number; supplyPercent?: number };
    top100?: { supply?: number; supplyPercent?: number };
    top250?: { supply?: number; supplyPercent?: number };
    top500?: { supply?: number; supplyPercent?: number };
    // Add more keys if needed
  };
}

const HolderData: React.FC<HolderProps> = ({ ca, chain }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [holderData, setHolderData] = useState<HolderDataResponse | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        // Call the Netlify Function instead of directly using the external API endpoint
        const response = await fetch(
          `/.netlify/functions/fetch-holder-data?ca=${ca}&chain=${chain}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }

        const data: HolderDataResponse = await response.json();
        setHolderData(data);
      } catch (err: any) {
        setError(err.message || 'An error occurred.');
      } finally {
        setLoading(false);
      }
    };

    if (ca && chain) {
      fetchData();
    }
  }, [ca, chain]);

  const formatVolume = (volume: number | undefined): string => {
    if (volume == null) return '-';

    if (Math.abs(volume) >= 1_000_000_000) {
      return (
        (volume / 1_000_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'B'
      );
    } else if (Math.abs(volume) >= 1_000_000) {
      return (
        (volume / 1_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'M'
      );
    } else if (Math.abs(volume) >= 1_000) {
      return (
        (volume / 1_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'K'
      );
    }
    return volume.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className='not-found'>We ran into an issue, please try again later... 😥</div>;
  }

  if (!holderData) {
    return <div>No holder data available yet... 😥</div>;
  }

  const swap = holderData.holdersByAcquisition?.swap ?? 0;
  const transfer = holderData.holdersByAcquisition?.transfer ?? 0;
  const airdrop = holderData.holdersByAcquisition?.airdrop ?? 0;

  const total = swap + transfer + airdrop || 1;
  const swapPercent = (swap / total) * 100;
  const transferPercent = (transfer / total) * 100;
  const airdropPercent = (airdrop / total) * 100;

  return (
    <>
      <div className="holder-wrapper">
        <div className="holder-boxes">
          {chain !== 'sol' && (
            <div className="holder-box">
              <div className="token-security">
                <div className="scanner-data">
                  <div>Total Holders</div>
                  <div className='scanner-right'>{holderData.totalHolders?.toLocaleString() ?? '-'}</div>
                </div>
                <div className="scanner-data">
                  <div>Top 10 holders</div>
                  <div className='scanner-right'>
                    {formatVolume(holderData.holderSupply?.top10?.supply)} (
                    {holderData.holderSupply?.top10?.supplyPercent ?? 0}%)
                  </div>
                </div>
                <div className="scanner-data">
                  <div>Top 25 holders</div>
                  <div className='scanner-right'>
                    {formatVolume(holderData.holderSupply?.top25?.supply)} (
                    {holderData.holderSupply?.top25?.supplyPercent ?? 0}%)
                  </div>
                </div>
                <div className="scanner-data">
                  <div>Top 50 holders</div>
                  <div className='scanner-right'>
                    {formatVolume(holderData.holderSupply?.top50?.supply)} (
                    {holderData.holderSupply?.top50?.supplyPercent ?? 0}%)
                  </div>
                </div>
                <div className="scanner-data">
                  <div>Top 100 holders</div>
                  <div className='scanner-right'>
                    {formatVolume(holderData.holderSupply?.top100?.supply)} (
                    {holderData.holderSupply?.top100?.supplyPercent ?? 0}%)
                  </div>
                </div>
                <div className="scanner-data">
                  <div>Top 250 holders</div>
                  <div className='scanner-right'>
                    {formatVolume(holderData.holderSupply?.top250?.supply)} (
                    {holderData.holderSupply?.top250?.supplyPercent ?? 0}%)
                  </div>
                </div>
                <div className="scanner-data">
                  <div>Top 500 holders</div>
                  <div className='scanner-right'>
                    {formatVolume(holderData.holderSupply?.top500?.supply)} (
                    {holderData.holderSupply?.top500?.supplyPercent ?? 0}%)
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="holder-box">
            <div className="token-security">
              <div className="scanner-data">
                <div>
                  {/* <div 
                    className="dex-title" 
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content=">0.1%"
                  >
                    <Info />
                  </div> */}
                  🐳 Whales <div className='holder-val'>0.1%</div>
                </div>
                <div>{holderData.holderDistribution?.whales?.toLocaleString() ?? '-'}</div>
              </div>

              <div className="scanner-data">
                <div>
                  {/* <div 
                    className="dex-title" 
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="0.05% - 0.1%"
                  >
                    <Info />
                  </div> */}
                  🦈 Sharks <div className='holder-val'>0.05%</div>
                </div>
                <div>{holderData.holderDistribution?.sharks?.toLocaleString() ?? '-'}</div>
              </div>

              <div className="scanner-data">
                <div>
                  {/* <div 
                    className="dex-title" 
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="0.005% - 0.05%"
                  >
                    <Info />
                  </div> */}
                  🐬 Dolphins <div className='holder-val'>0.005%</div>
                </div>
                <div>{holderData.holderDistribution?.dolphins?.toLocaleString() ?? '-'}</div>
              </div>

              <div className="scanner-data">
                <div>
                  {/* <div 
                    className="dex-title" 
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="0.0001% - 0.005%"
                  >
                    <Info />
                  </div> */}
                  🐟 Fish <div className='holder-val'>0.0001%</div>
                </div>
                <div>{holderData.holderDistribution?.fish?.toLocaleString() ?? '-'}</div>
              </div>

              <div className="scanner-data">
                <div>
                  {/* <div 
                    className="dex-title" 
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="0.00025% - 0.0001%"
                  >
                    <Info />
                  </div> */}
                  🐙 Octopus <div className='holder-val'>0.00025%</div>
                </div>
                <div>{holderData.holderDistribution?.octopus?.toLocaleString() ?? '-'}</div>
              </div>

              <div className="scanner-data">
                <div>
                  {/* <div 
                    className="dex-title" 
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="0.00005% - 0.00025%"
                  >
                    <Info />
                  </div> */}
                  🦀 Crabs <div className='holder-val'>0.00005%</div>
                </div>
                <div>{holderData.holderDistribution?.crab?.toLocaleString() ?? holderData.holderDistribution?.crabs?.toLocaleString() ?? '-'}</div>
              </div>

              <div className="scanner-data">
                <div>
                  {/* <div 
                    className="dex-title" 
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="0% - 0.00005%"
                  >
                    <Info />
                  </div> */}
                  🦐 Shrimps <div className='holder-val'>0%</div>
                </div>
                <div>{holderData.holderDistribution?.shrimps?.toLocaleString() ?? '-'}</div>
              </div>
            </div>
          </div>

          <div className="holder-box">
            <div className="token-security">
              {Object.entries(holderData.holderChange ?? {}).map(([timeframe, stats]) => {
                const changeColor =
                  stats.change === 0
                    ? 'inherit'
                    : stats.change > 0
                    ? 'rgb(14, 203, 129)'
                    : 'rgb(246, 70, 93)';
                const percentColor =
                  stats.changePercent === 0
                    ? 'inherit'
                    : stats.changePercent > 0
                    ? 'rgb(14, 203, 129)'
                    : 'rgb(246, 70, 93)';

                const displayChange =
                  stats.change >= 0 ? `+${stats.change}` : `${stats.change}`;
                const displayPercent =
                  stats.changePercent >= 0 ? `+${stats.changePercent}%` : `${stats.changePercent}%`;

                return (
                  <div className="scanner-data" key={timeframe}>
                    <div>{timeframe} holders</div>
                    <div>
                      <div style={{ color: changeColor }}>{displayChange}</div>{' '}
                      <div style={{ color: percentColor }}>({displayPercent})</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Acquisition Bar */}
        <div className="acquisition">
          <ProgressBar className='progress-acq'>
            <ProgressBar
              now={swapPercent}
              style={{ backgroundColor: "#378e73"}}
              label={`${swapPercent.toFixed(0)}%`}
              key={1}
            />
            <ProgressBar
              now={transferPercent}
              style={{ backgroundColor: "#21354d"}}
              label={`${transferPercent.toFixed(0)}%`}
              key={2}
            />
            <ProgressBar
              now={airdropPercent}
              style={{ backgroundColor: "#55147e"}}
              label={`${airdropPercent.toFixed(0)}%`}
              key={3}
            />
          </ProgressBar>
          <div className="legenda">
            <p>
              <Square style={{ color: '#378e73' }} /> Holder by Swaps
            </p>
            <p>
              <Square style={{ color: '#21354d' }} /> Holder by Transfers
            </p>
            <p>
              <Square style={{ color: '#55147e' }} /> Holder by Airdrop
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default HolderData;
